import Loading from 'components/Loading'
import { useObserver } from 'mobx-react-lite'
import GenderSwitch from 'pages/SeoStreamPage/components/GenderSwitch'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router'
import MixedTileRow from 'sharedComponents/ProductStream/MixedTileRow'
import ProductStreamFooter from 'sharedComponents/ProductStream/ProductStreamFooter'
import Stream from 'sharedComponents/ProductStream/Stream'
import { useStreamIndexOffset } from 'sharedComponents/ProductStream/helpers'
import {
  useSearchPageStore,
  useSeoStreamPageStore,
  useUserStore,
} from 'store/context'
import { ActiveImageTypeContext } from 'store/features/imageView/context'
import styled from 'styled-components'
import { HeadingM } from 'uiComponents/atoms/Text/Headings'
import { RowTypes } from 'uiComponents/molecules/TileGrid/RowBuilder'
import EmptyResult from './components/EmptyResult/EmptyResult'

const LoadingWrapper = styled.div`
  height: 100%;
  position: relative;
`

const SeoHeading = styled(HeadingM.withComponent('h1'))`
  display: inline-block;
`

const StyledGenderSwitch = styled(GenderSwitch)`
  margin-left: 24px;
`

const HeadingGenderSwitchWrapper = styled.p`
  padding: 24px 0;
`

const SeoStreamProductList: React.FC = () => {
  const {
    activeImageType,
    streamLoading,
    productIds,
    rowBuilder,
    resultIsEmpty,
  } = useSeoStreamPageProductListData()
  const searchPageStore = useSearchPageStore()
  const { search } = useLocation()
  const { productIndexOffset, rowIndexOffset } = useStreamIndexOffset(
    searchPageStore.pagination,
  )
  const { headline: seoPageHeadline, name: seoPageName } =
    useSeoStreamPageStore()

  const queryParams = new URLSearchParams(search)
  const gender = queryParams.get('gender')

  const userStore = useUserStore()
  useEffect(() => {
    if (gender) {
      userStore.setGender(gender)
    }
  }, [gender])

  if (streamLoading && !productIds.length) {
    return (
      <LoadingWrapper>
        <Loading isCentered={true} />
      </LoadingWrapper>
    )
  }

  return (
    <ActiveImageTypeContext.Provider value={activeImageType}>
      <HeadingGenderSwitchWrapper>
        <SeoHeading>{seoPageHeadline || seoPageName}</SeoHeading>
        <StyledGenderSwitch />
      </HeadingGenderSwitchWrapper>

      {resultIsEmpty ? (
        <EmptyResult />
      ) : (
        <Stream
          loading={streamLoading}
          firstBatchProductIds={productIds}
          rowBuilder={rowBuilder}
          rowComponentMap={{
            [RowTypes.StreamRow]: MixedTileRow,
          }}
          productIndexOffset={productIndexOffset}
          rowIndexOffset={rowIndexOffset}
          setProductCurrencyCode={(currencyCode: string) => {
            searchPageStore.setProductCurrencyCode(currencyCode)
          }}
        />
      )}
      <ProductStreamFooter />
    </ActiveImageTypeContext.Provider>
  )
}

const useSeoStreamPageProductListData = () => {
  const searchPageStore = useSearchPageStore()
  const resultIsEmpty = useObserver(
    () => searchPageStore.pagination.totalPages === 0,
  )

  return useObserver(() => ({
    activeImageType: searchPageStore.activeImageType,
    streamLoading: searchPageStore.streamLoading,
    productIds: searchPageStore.productIds,
    rowBuilder: searchPageStore.rowBuilder,
    resultIsEmpty,
  }))
}

export default SeoStreamProductList
